import { Component, computed, DestroyRef, effect, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { GenericDropdownComponent, InputFieldComponent, rxResourceFixed } from '@zayan-erp/shared';
import { selectCategoryListByProductListId } from '@zayan-erp/stores';
import { ProjectsService } from '../../services/projects.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as HomeBuildingActions from '@zayan-erp/stores';
import { AsyncPipe } from '@angular/common';
import { Elevation, ElevationService, Estimate, EstimateDto, RoomConfigurationService } from '@zayan-erp/home-building';
import { DropdownData } from '@zayan-erp/shared';
import { of } from 'rxjs';

const ALL_OPTION_ID = '00000000-0000-0000-0000-000000000000';
type EstimateFormValue = Pick<EstimateDto, 'categoryId' | 'subCategoryId' | 'uofmId' | 'quantity'>;

@Component({
  selector: 'lib-room-configuration-form',
  imports: [InputFieldComponent, GenericDropdownComponent, AsyncPipe, FormsModule, ReactiveFormsModule],
  standalone: true,
  templateUrl: './room-configuration-form.component.html'
})
export class RoomConfigurationFormComponent {
  #store = inject(Store);
  #modelService = inject(ProjectsService);
  #elevationService = inject(ElevationService);
  #destroy = inject(DestroyRef);
  #roomConfigService = inject(RoomConfigurationService);
  formSubmit = output<any>();
  closeForm = output<void>();
  modelId = input<string | null>(null);
  form = new FormGroup({
    seqNo: new FormControl<string | null>(null, [Validators.required]),
    locationId: new FormControl<string | null>(null, [Validators.required]),
    elevationId: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null),
    estimates: new FormControl<Estimate[]>([], [Validators.required])
  });

  estimate = new FormGroup({
    categoryId: new FormControl<string | null>(null, [Validators.required]),
    subCategoryId: new FormControl<string | null>(null, [Validators.required]),
    uofmId: new FormControl<string | null>(null, [Validators.required]),
    quantity: new FormControl<number | null>(null)
  });

  estimateArray: EstimateDto[] = [];
  editEstimateId: string | null = null;
  categoryListByProductListId$ = this.#store.select(
    selectCategoryListByProductListId(this.#modelService.selectedProductListId()!)
  );

  subcategories$ = this.#store.select(HomeBuildingActions.selectSubcategories(this.estimate.value.categoryId!));
  projectLocations$ = this.#store.select(HomeBuildingActions.selectProjectLocations);

  elevationsResource = rxResourceFixed({
    params: () => this.modelId(),
    defaultValue: [] as Elevation[],
    stream: ({ params }) => {
      if (!params) {
        return of([] as Elevation[]);
      }
      return this.#elevationService.readElevationByModelId(params);
    }
  });

  elevationsDisplay = computed(() => {
    const elevations = this.elevationsResource.value() ?? [];
    return elevations.map(elevation => ({
      id: elevation.id,
      firstLabel: elevation.elevationCode,
      secondLabel: elevation.description || elevation.elevationCode
    })) as DropdownData[];
  });

  uofms$ = this.#store.select(HomeBuildingActions.selectUofms);

  constructor() {
    this.#store.dispatch(HomeBuildingActions.loadProjectLocations());
    this.#store.dispatch(HomeBuildingActions.loadUofms());
    effect(() => {
      if (this.#modelService.selectedProductListId()) {
        this.#store.dispatch(
          HomeBuildingActions.loadCategoryListByProductListId({
            productListId: this.#modelService.selectedProductListId()!
          })
        );
      }
      this.categoryListByProductListId$ = this.#store.select(
        selectCategoryListByProductListId(this.#modelService.selectedProductListId()!)
      );
    });

    this.estimate
      .get('categoryId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroy))
      .subscribe((categoryId: string | null) => {
        this.syncSubCategoryForCategory(categoryId);
      });

    effect(() => {
      const roomConfig = this.#roomConfigService.estimatesToEdit();
      if (roomConfig) {
        this.form.patchValue({
          seqNo: roomConfig.seqNo,
          locationId: roomConfig.location?.id,
          elevationId: roomConfig.elevationId ?? roomConfig.elevation?.id ?? null,
          description: roomConfig.description,
          estimates: roomConfig.estimates as Estimate[]
        });
        this.estimateArray = roomConfig.estimates || [];
      }
    });
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.formSubmit.emit(this.form.getRawValue());
      this.form.reset();
      this.estimateArray = [];
    }
  }

  close() {
    this.form.reset();
    this.resetEstimateForm();
    this.estimateArray = [];
    this.emitEstimates();
    this.#roomConfigService.estimatesToEdit.set(null);
    this.closeForm.emit();
  }

  private syncSubCategoryForCategory(categoryId: string | null, subCategoryId?: string | null): void {
    if (!categoryId) {
      this.estimate.get('subCategoryId')?.setValue(null, { emitEvent: false });
      this.subcategories$ = of([]);
      return;
    }

    if (categoryId !== ALL_OPTION_ID && this.#modelService.selectedProductListId()) {
      this.#store.dispatch(
        HomeBuildingActions.loadSubcategories({
          productListId: this.#modelService.selectedProductListId()!,
          categoryListId: categoryId
        })
      );
      this.subcategories$ = this.#store.select(HomeBuildingActions.selectSubcategories(categoryId));
    } else {
      this.subcategories$ = of([]);
    }

    this.estimate.get('subCategoryId')?.setValue(subCategoryId ?? ALL_OPTION_ID, { emitEvent: false });
  }

  private resetEstimateForm(): void {
    this.estimate.reset(
      {
        categoryId: null,
        subCategoryId: null,
        uofmId: null,
        quantity: null
      },
      { emitEvent: false }
    );
    this.editEstimateId = null;
  }

  getCategoryDescription(categories: DropdownData[] | null, categoryCode?: string): string {
    if (!categories || !categoryCode) return '';
    if (categoryCode === '00000000-0000-0000-0000-000000000000') return 'All Categories';
    const category = categories.find(cat => cat.id === categoryCode);
    return category?.secondLabel || category?.firstLabel || '';
  }

  getSubCategoryDescription(subcategories: DropdownData[] | null, subCategoryCode?: string): string {
    if (!subcategories || !subCategoryCode) return '';
    if (subCategoryCode === '00000000-0000-0000-0000-000000000000') return 'All Subcategories';
    const subCategory = subcategories?.find(subCat => subCat.id === subCategoryCode);
    return subCategory?.firstLabel || subCategory?.secondLabel || '';
  }

  getUofmDescription(uofms: DropdownData[] | null, uofmCode?: string): string {
    if (!uofms || !uofmCode) return '';
    const uofm = uofms.find(uofm => uofm.id === uofmCode);
    return uofm?.firstLabel || uofm?.secondLabel || '';
  }

  getEstimateCategoryCode(estimate: EstimateDto): string {
    return estimate.category?.code || '';
  }

  getEstimateSubCategoryCode(estimate: EstimateDto): string {
    return estimate.subCategory?.code || '';
  }

  getEstimateUofmCode(estimate: EstimateDto): string {
    return estimate.uofm?.code || '';
  }

  removeEstimate(estimateId: string) {
    this.estimateArray = this.estimateArray.filter(estimate => estimate.id !== estimateId);
    this.emitEstimates();
  }

  editEstimate(estimate: EstimateDto) {
    this.editEstimateId = estimate.id;
    const categoryId = estimate.categoryId ?? (estimate.applyToAllCategories ? ALL_OPTION_ID : null);
    const subCategoryId = categoryId
      ? (estimate.subCategoryId ?? (estimate.applyToAllSubCategories ? ALL_OPTION_ID : null))
      : null;

    this.syncSubCategoryForCategory(categoryId, subCategoryId);
    this.estimate.patchValue(
      {
        categoryId,
        subCategoryId,
        uofmId: estimate.uofmId,
        quantity: estimate.quantity
      },
      { emitEvent: false }
    );
  }

  addEstimate() {
    if (this.isEstimateFormInvalid()) {
      return;
    }
    this.estimateArray.push({
      ...this.getEstimateFormValue(),
      id: crypto.randomUUID()
    } as EstimateDto);
    this.resetEstimateForm();
    this.emitEstimates();
  }

  updateEstimate() {
    if (this.isEstimateFormInvalid()) {
      return;
    }
    const updatedValues = this.getEstimateFormValue();
    this.estimateArray = this.estimateArray.map(estimate =>
      estimate.id === this.editEstimateId ? { ...estimate, ...updatedValues } : estimate
    );
    this.emitEstimates();
    this.resetEstimateForm();
  }

  private isEstimateFormInvalid(): boolean {
    this.estimate.markAllAsTouched();
    return this.estimate.invalid;
  }

  private getEstimateFormValue(): EstimateFormValue {
    return this.estimate.getRawValue() as EstimateFormValue;
  }

  private emitEstimates(): void {
    this.form.controls.estimates.setValue(this.estimateArray as Estimate[]);
  }
}
